<!-- 単語練習モーダル -->
<template>
  <div v-if="isOpen" class="word-practice-modal-overlay" @click.self="closeModal">
    <div class="word-practice-modal">
      <div class="modal-header">
        <h2>単語練習: "{{ word?.word || word?.expectedWord || '' }}"</h2>
        <button @click="closeModal" class="close-button">×</button>
      </div>
      
      <div class="modal-content">
        <!-- 音素詳細表示 -->
        <div class="phoneme-details" v-if="evaluation && evaluation.phonemes && evaluation.phonemes.length > 0">
          <div class="phoneme-row">
            <div
              v-for="(p, i) in evaluation.phonemes"
              :key="`phoneme-${i}`"
              class="phoneme-inline-card"
            >
              <span class="phoneme-inline-symbol">{{ p.phoneme }}</span>
              <span class="phoneme-inline-score" :class="getPhonemeClass(p.accuracy)">
                {{ p.accuracy !== undefined ? Math.round(p.accuracy) : '-' }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- 状態表示 -->
        <div class="session-status">
          <div class="status-indicator" :class="sessionState.toLowerCase()">
            <span v-if="sessionState === 'IDLE'">⏸️ 停止中</span>
            <span v-else-if="sessionState === 'RECORDING'">🎤 音声入力中</span>
            <span v-else-if="sessionState === 'EVALUATING'">⏳ 評価中...</span>
            <span v-else-if="sessionState === 'PLAYING'">🔊 お手本再生中</span>
          </div>
          <div class="session-timer" v-if="sessionState !== 'IDLE' && sessionState !== 'SESSION_END'">
            {{ sessionTime }}秒経過
          </div>
        </div>
        
        <!-- コントロール -->
        <div class="session-controls">
          <button v-if="sessionState === 'IDLE'" @click="startSession" class="control-button start-button">
            ▶️ 練習開始
          </button>
          <button v-else-if="sessionState !== 'SESSION_END'" @click="endSession" class="control-button stop-button">
            ⏹️ 練習終了
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import {
  AzureSpeechEvaluator,
  normalizeSingleWord,
  getDictionaryAudioUrl,
  loadSpeechSDK,
  destroyStickyRecognizer
} from '../utils/azureSpeechEvaluator'

const props = defineProps({
  word: { type: Object, default: null },
  isOpen: { type: Boolean, default: false }
})

const emit = defineEmits(['close'])

const speechEvaluator = new AzureSpeechEvaluator()

const SESSION_TIMEOUT = 30000 // 30秒

const sessionState = ref('IDLE') // IDLE | PLAYING | RECORDING | EVALUATING | SESSION_END
const evaluation = ref(null)
const running = ref(false)
const sessionStartTime = ref(0)
const sessionTime = ref(0)

const wordText = computed(() => props.word?.word || props.word?.expectedWord || '')

const cachedWord = ref(null)
const cachedUrl = ref(null)

let sessionTimer = null
let loopTask = null
let currentRecognitionCancel = null

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

function resetSession() {
  sessionState.value = 'IDLE'
  evaluation.value = null
  sessionTime.value = 0
  sessionStartTime.value = 0
}

async function prefetchDictionaryAudio(word) {
  if (!word) return
  try {
    const url = await getDictionaryAudioUrl(word)
    if (url) {
      cachedWord.value = word
      cachedUrl.value = url
      console.log('✅ 辞書URL事前プリフェッチ完了:', { word, url })
      try {
        await fetch(url, { cache: 'force-cache', mode: 'cors' })
        console.log('♻️ 辞書音声キャッシュウォームアップ完了')
      } catch (cacheErr) {
        console.warn('⚠️ 辞書音声キャッシュウォームアップ失敗:', cacheErr)
      }
    }
  } catch (error) {
    console.warn('⚠️ 辞書URL事前プリフェッチ失敗:', error)
  }
}

async function ensureConnectionReady() {
  await loadSpeechSDK()
  if (typeof speechEvaluator.ensureValidToken === 'function') {
    await speechEvaluator.ensureValidToken()
  }
  if (typeof speechEvaluator.ensureStickyReady === 'function') {
    await speechEvaluator.ensureStickyReady('en-US')
  }
  if (wordText.value && typeof speechEvaluator.setStickyReferenceText === 'function') {
    await speechEvaluator.setStickyReferenceText(wordText.value)
  }
  if (typeof speechEvaluator.stopContinuousRecognition === 'function') {
    try {
      await speechEvaluator.stopContinuousRecognition()
    } catch (err) {
      console.warn('⚠️ stopContinuousRecognition 事前停止エラー:', err)
    }
  }
}

async function playSampleAudio() {
  if (!cachedUrl.value || !cachedWord.value) {
    throw new Error('辞書音声が準備されていません')
  }
  sessionState.value = 'PLAYING'
  const result = await speechEvaluator.playReferenceAudio(
    wordText.value,
    cachedUrl.value,
    cachedWord.value
  )
  if (!result.ok) {
    throw new Error(result.message || 'サンプル音声の再生に失敗しました')
  }
}

async function parseSdkPronunciationResult(sdkResult) {
  if (!sdkResult) return null
  const text = sdkResult.text?.trim()
  if (!text) {
    console.warn('⚠️ 認識テキストが空でした')
    return null
  }

  const speechsdk = await loadSpeechSDK()

  let rawJsonData = null
  try {
    const raw = sdkResult.properties?.getProperty(
      speechsdk.PropertyId.SpeechServiceResponse_JsonResult
    )
    rawJsonData = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
  } catch (err) {
    console.warn('⚠️ RAW JSONパースエラー:', err)
  }
  
  try {
    const pa = speechsdk.PronunciationAssessmentResult.fromResult(sdkResult)
    if (typeof speechEvaluator.parseSDKResult === 'function') {
      return speechEvaluator.parseSDKResult(pa, wordText.value, rawJsonData)
    }
    return {
      overall: pa.pronunciationScore ?? null,
      accuracy: pa.accuracyScore ?? null,
      fluency: pa.fluencyScore ?? null,
      completeness: pa.completenessScore ?? null,
      phonemes: []
    }
  } catch (err) {
    console.error('❌ SDK結果の解析に失敗:', err)
    return null
  }
}

async function waitForPronunciation() {
  if (typeof speechEvaluator.startContinuousRecognition !== 'function') {
    throw new Error('連続認識APIが利用できません')
  }

  sessionState.value = 'RECORDING'

  if (wordText.value && typeof speechEvaluator.setStickyReferenceText === 'function') {
    await speechEvaluator.setStickyReferenceText(wordText.value)
  }

  return new Promise((resolve, reject) => {
    let finished = false

    const finish = async (value, isError = false) => {
      if (finished) return
      finished = true
      currentRecognitionCancel = null
      try {
        if (typeof speechEvaluator.stopContinuousRecognition === 'function') {
          await speechEvaluator.stopContinuousRecognition()
        }
      } catch (stopErr) {
        console.warn('⚠️ stopContinuousRecognition エラー:', stopErr)
      }

      if (isError) {
        reject(value)
      } else {
        resolve(value)
      }
    }

    currentRecognitionCancel = () => finish(new Error('SESSION_STOPPED'), true)

    const onRecognized = async (sdkResult) => {
      if (finished) return
      try {
        const parsed = await parseSdkPronunciationResult(sdkResult)
        sessionState.value = 'EVALUATING'
        await finish(parsed, false)
      } catch (err) {
        await finish(err, true)
      }
    }

    speechEvaluator
      .startContinuousRecognition(wordText.value, null, onRecognized)
      .catch(async (err) => {
        await finish(err, true)
      })
  })
}

async function applyEvaluation(result) {
  if (!result) {
    evaluation.value = null
    sessionState.value = 'IDLE'
    return
  }
  
  evaluation.value = {
    overall: result.overall ?? null,
    accuracy: result.accuracy ?? null,
    fluency: result.fluency ?? null,
    completeness: result.completeness ?? null,
    phonemes: Array.isArray(result.phonemes) ? result.phonemes : []
  }

  await sleep(120)
  sessionState.value = 'IDLE'
}

function userMessageForError(error) {
  if (!error) return 'エラーが発生しました。'
  if (error.message === 'SESSION_STOPPED') return 'セッションを終了しました。'

  const code = error.code || error.errorType
  if (code === 'NO_AUTH_SESSION') {
    return 'ログインセッションが切れています。ページを再読み込みしてください。'
  }
  if (code === 'TOKEN_FETCH_ERROR' || code === 'AUTH_FAILED') {
    return 'Azureトークンの取得に失敗しました。ネットワーク接続を確認してください。'
  }
  if (code === 'MICROPHONE_PERMISSION_DENIED' || code === 'MICROPHONE_NOT_FOUND') {
    return error.message || 'マイクへのアクセスが拒否されました。ブラウザの設定でマイクの権限を許可してください。'
  }
  if (code === 'CONNECTION_ERROR' || error.message?.includes('WebSocket')) {
    return 'Azure Speech Serviceへの接続に失敗しました。ネットワーク接続を確認してください。'
  }
  return error.message || 'エラーが発生しました。'
}

async function runPracticeLoop() {
  sessionStartTime.value = Date.now()
  sessionTime.value = 0
  sessionTimer = setInterval(() => {
    sessionTime.value = Math.floor((Date.now() - sessionStartTime.value) / 1000)
  }, 1000)
  
  try {
    while (running.value && (Date.now() - sessionStartTime.value) < SESSION_TIMEOUT) {
      try {
        if (typeof speechEvaluator.stopContinuousRecognition === 'function') {
          await speechEvaluator.stopContinuousRecognition()
        }
      } catch (err) {
        console.warn('⚠️ stopContinuousRecognition ループ開始時エラー:', err)
      }

      await playSampleAudio()
      if (!running.value) break

      let result = null
      try {
        result = await waitForPronunciation()
      } catch (err) {
        if (err?.message === 'SESSION_STOPPED' || !running.value) {
          break
        }
        console.error('❌ 認識エラー:', err)
        alert(userMessageForError(err))
        sessionState.value = 'IDLE'
        continue
      }

      if (!running.value) break
      await applyEvaluation(result)
      await sleep(160) // UIを見やすくするため軽く待機
    }
  } catch (err) {
    if (err?.message !== 'SESSION_STOPPED') {
      console.error('❌ 練習ループ中にエラー:', err)
      alert(userMessageForError(err))
    }
  } finally {
    if (sessionTimer) {
      clearInterval(sessionTimer)
      sessionTimer = null
    }
    running.value = false
    currentRecognitionCancel = null
    await stopRecognitionSilently()
    sessionState.value = 'SESSION_END'
    setTimeout(() => {
    sessionState.value = 'IDLE'
    sessionTime.value = 0
    }, 400)
  }
}

const startSession = async () => {
  if (running.value || sessionState.value !== 'IDLE') return

  if (!wordText.value) {
    alert('練習対象の単語が選択されていません。')
    return
  }
  
  if (!cachedUrl.value) {
    const normalized = normalizeSingleWord(wordText.value)
    if (!normalized) {
      alert('辞書音声は現在「単語のみ」対応です。')
      return
    }
    await prefetchDictionaryAudio(normalized)
  }

  if (!cachedUrl.value) {
    alert('辞書音声の取得に失敗しました。')
    return
  }
  
  try {
    await speechEvaluator.unlockIOSPlayback()
    await ensureConnectionReady()
    evaluation.value = null
    running.value = true

    loopTask = runPracticeLoop()
    loopTask.finally(() => {
      loopTask = null
    })
  } catch (error) {
    console.error('❌ セッション開始エラー:', error)
    alert(userMessageForError(error))
    running.value = false
  }
}

async function stopRecognitionSilently() {
  if (typeof speechEvaluator.stopContinuousRecognition === 'function') {
    try {
      await speechEvaluator.stopContinuousRecognition()
    } catch (err) {
      console.warn('⚠️ stopContinuousRecognition 停止エラー:', err)
    }
  }
}

async function cleanupStickyRecognizer() {
  if (typeof destroyStickyRecognizer === 'function') {
    try {
      await destroyStickyRecognizer()
    } catch (err) {
      console.warn('⚠️ Sticky recognizer 解放エラー:', err)
    }
  }
}

const endSession = async () => {
  if (currentRecognitionCancel) {
    currentRecognitionCancel()
  }

  running.value = false

  if (loopTask) {
    try {
      await loopTask
    } catch (err) {
      if (err?.message !== 'SESSION_STOPPED') {
        console.warn('⚠️ 練習ループ終了時エラー:', err)
      }
    }
    loopTask = null
  }

  await stopRecognitionSilently()

  if (sessionTimer) {
    clearInterval(sessionTimer)
    sessionTimer = null
  }
  
  await cleanupStickyRecognizer()
}

const closeModal = async () => {
    await endSession()
  emit('close')
}

const getPhonemeClass = (accuracy) => {
  if (accuracy === null || accuracy === undefined) return 'phoneme-no-score'
  if (accuracy >= 80) return 'phoneme-good'
  if (accuracy >= 60) return 'phoneme-fair'
  return 'phoneme-poor'
}

const handleVisibilityChange = () => {
  if (document.hidden && running.value) {
    endSession()
  }
}

const handlePageHide = () => {
  if (running.value) {
    endSession()
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)
})

onBeforeUnmount(async () => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pagehide', handlePageHide)
      await endSession()
  await speechEvaluator.cleanup?.()
})

watch(() => props.isOpen, async (open) => {
  console.log('🎯 WordPracticeModal isOpen:', { open, word: props.word })

  if (open) {
    resetSession()
    await speechEvaluator.initializeMode?.()

    const normalized = normalizeSingleWord(wordText.value)
    cachedWord.value = null
    cachedUrl.value = null

    if (normalized) {
      await prefetchDictionaryAudio(normalized)
    }
  } else {
    await endSession()
    cachedWord.value = null
    cachedUrl.value = null
    evaluation.value = null
    sessionTime.value = 0
  }
})

</script>



<style scoped>
.word-practice-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.word-practice-modal {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-button:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.pronunciation-comparison {
  padding: 1rem;
  background: #fff7ed;
  border-radius: 8px;
  border: 2px solid #fed7aa;
  margin-bottom: 1rem;
}

.comparison-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.comparison-row:last-child {
  margin-bottom: 0;
}

.comparison-label {
  font-weight: bold;
  font-size: 0.9rem;
  color: #92400e;
  min-width: 140px;
}

.comparison-value {
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  flex: 1;
}

.comparison-value.expected {
  background: #dbeafe;
  color: #1e40af;
  border: 2px solid #93c5fd;
}

.comparison-value.recognized {
  background: #fef3c7;
  color: #92400e;
  border: 2px solid #fcd34d;
}

.phoneme-details {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.phoneme-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.phoneme-inline-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.35rem 0.45rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  font-size: 0.82rem;
}

.phoneme-inline-symbol {
  font-weight: 600;
  color: #1f2937;
}

.phoneme-inline-score {
  font-weight: 600;
  font-size: 0.78rem;
}

.session-status {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.status-indicator {
  font-weight: bold;
  font-size: 1.1rem;
  text-align: center;
  padding: 0.75rem;
  border-radius: 8px;
  background: white;
  border: 2px solid #e5e7eb;
}

.status-indicator.idle {
  color: #6b7280;
  background: #f3f4f6;
}

.status-indicator.recording {
  color: #2563eb;
  background: #dbeafe;
  border-color: #93c5fd;
  animation: pulse 2s infinite;
}

.status-indicator.evaluating {
  color: #f59e0b;
  background: #fef3c7;
  border-color: #fcd34d;
  animation: pulse 1s infinite;
}

.status-indicator.playing {
  color: #059669;
  background: #d1fae5;
  border-color: #6ee7b7;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.session-timer {
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
}

.session-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.control-button {
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.start-button {
  background: #2563eb;
  color: white;
}

.start-button:hover {
  background: #1d4ed8;
  transform: scale(1.05);
}

.stop-button {
  background: #dc2626;
  color: white;
}

.stop-button:hover {
  background: #b91c1c;
  transform: scale(1.05);
}

.phoneme-inline-score.phoneme-good {
  color: #10b981;
}

.phoneme-inline-score.phoneme-fair {
  color: #f59e0b;
}

.phoneme-inline-score.phoneme-poor {
  color: #ef4444;
}

.phoneme-inline-score.phoneme-no-score {
  color: #6b7280;
}

.recording-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.record-button {
  padding: 1rem 2rem;
  font-size: 1.2rem;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: #3b82f6;
  color: white;
}

.record-button:hover:not(:disabled) {
  background: #2563eb;
}

.record-button.recording {
  background: #ef4444;
}

.record-button.recording:hover {
  background: #dc2626;
}

.record-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.recording-timer {
  font-size: 1.5rem;
  font-weight: bold;
  color: #6b7280;
}

/* 追加：スコア表示用 */
.score-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.score-card {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.score-card span {
  color: #6b7280;
  font-weight: 600;
}
.score-card strong {
  font-size: 1.25rem;
  color: #111827;
}

</style>

